import { useState, useEffect, useCallback } from 'react'
import {
    Alert,
    Box,
    Checkbox,
    Container,
    FormControlLabel,
    FormGroup,
    Link,
    Paper,
    Stack,
    Typography,
    useTheme,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import toast from 'react-hot-toast';
import { Seo } from 'src/components/seo';
import { usePageView } from 'src/hooks/use-page-view';
import { HomeCta } from '../sections/home/home-cta';
import { useAuth } from 'src/hooks/use-auth';
import { profileApi } from 'src/api/profile';
import { tr } from 'src/locales/tr';

const notifOptions = [
    'Immediately',
    tr("Once a day"),
    tr("Once every three days"),
    tr("Once a week"),
    tr("Once a month"),
    tr("I do not want to receive any notifications"),
];

const Page = () => {
    usePageView();
    const theme = useTheme();

    const { user } = useAuth();
    const [agreeTos, setAgreeTos] = useState(window.localStorage.getItem('agreedToTerms') === 'true');
    const [notifChoice, setNotifChoice] = useState(null);

    useEffect(() => {
        if (!user?.id) return;
        (async () => {
            const snap = await profileApi.get(user.id);
            if (!snap) return;
            setAgreeTos(!!snap.agreedToTerms);
            setNotifChoice(snap.notificationOption ?? null);
            window.localStorage.setItem('agreedToTerms', snap.agreedToTerms ? 'true' : 'false');
        })();
    }, [user?.id]);

    const handleAgreeChange = useCallback((event) => {
        const checked = event.target.checked;
        setAgreeTos(checked);
        window.localStorage.setItem('agreedToTerms', checked ? 'true' : 'false');
        if (user?.id) profileApi.setTermsAgreement(user.id, checked);
        if (checked) {
            toast.success(tr("Thank you! Your acceptance of the Terms and Conditions has been recorded."));
        }
    }, [user?.id]);

    const handleNotifChange = (value) => () => {
        setNotifChoice(prev => {
            const next = prev === value ? null : value;
            if (user?.id) profileApi.setNotificationOption(user.id, next);
            return next;
        });
    };

    return (
        <>
            <Seo title={tr("Terms and Conditions")} />
            <Box component="main" sx={{ flexGrow: 1 }}>
                {/* Header */}
                <Box
                    sx={{
                        backgroundColor: theme.palette.mode === 'dark' ? 'neutral.800' : 'neutral.50',
                        pb: 6,
                        pt: 18,
                        textAlign: 'center',
                    }}
                >
                    <Container maxWidth="lg">
                        <Stack spacing={1} alignItems="center">
                            <Typography variant="h1">{tr("Terms and Conditions")}</Typography>
                            <Typography
                                variant="body1"
                                sx={{ color: theme.palette.text.secondary }}
                            >
                                {tr("These Terms and Conditions (“Terms”) govern your use of the CTMASS website and mobile apps (together, the “Platform”), operated by CTMASS LLC, a Connecticut limited liability company (“CTMASS,” “we,” “our,” or “us”). Please read them carefully before using the Platform.")}
                                <br />
                                {tr("By accessing or using the Platform, you agree to these Terms. If you do not agree, you must not use the Platform.")}
                            </Typography>
                        </Stack>
                    </Container>
                </Box>
                <Box
                    sx={{
                        backgroundColor: theme.palette.mode === 'dark' ? 'neutral.900' : 'neutral.100',
                        py: 4,
                    }}
                >
                    <Container maxWidth="xl">
                        <Stack spacing={1}>
                            {/* Term 1 */}
                            <Paper
                                elevation={2}
                                sx={{
                                    p: 4,
                                    borderRadius: 2,
                                    backgroundColor: theme.palette.mode === 'dark' ? 'neutral.800' : 'common.white',
                                }}
                            >
                                <Typography variant="h5" gutterBottom>
                                    {tr("1. Introduction")}
                                </Typography>
                                <Typography
                                    variant="body1"
                                    sx={{
                                        color: theme.palette.text.secondary,
                                        lineHeight: 1.7,
                                    }}
                                >
                                    {tr("CTMASS.com is a community-based platform designed to connect homeowners and contractors in Connecticut and Massachusetts. Our mission is to help homeowners find reputable contractors and to help contractors discover new job opportunities. CTMASS provides only the connection and is not a party to any agreement between contractors and homeowners.")}
                                </Typography>
                            </Paper>

                            {/* Term 2 */}
                            <Paper
                                elevation={2}
                                sx={{
                                    p: 4,
                                    borderRadius: 2,
                                    backgroundColor: theme.palette.mode === 'dark' ? 'neutral.800' : 'common.white',
                                }}
                            >
                                <Typography variant="h5" gutterBottom>
                                    {tr("2. Eligibility")}
                                </Typography>
                                <Typography
                                    variant="body1"
                                    sx={{
                                        color: theme.palette.text.secondary,
                                        lineHeight: 1.7,
                                    }}
                                >
                                    {tr("You must be at least 18 years old to use the Platform. By using the Platform, you represent that you are legally able to enter into binding contracts.")}
                                </Typography>
                            </Paper>

                            {/* Term 3 */}
                            <Paper
                                elevation={2}
                                sx={{
                                    p: 4,
                                    borderRadius: 2,
                                    backgroundColor: theme.palette.mode === 'dark' ? 'neutral.800' : 'common.white',
                                }}
                            >
                                <Typography variant="h5" gutterBottom>
                                    {tr("3. Account Creation and Security")}
                                </Typography>
                                <Typography
                                    variant="body1"
                                    sx={{
                                        color: theme.palette.text.secondary,
                                        lineHeight: 1.7,
                                    }}
                                >
                                    {tr("To access certain features, you may need to create an account. You are responsible for safeguarding your login information and for all activity under your account. Notify us immediately of any unauthorized use.")}
                                </Typography>
                            </Paper>

                            {/* Term 4 */}
                            <Paper
                                elevation={2}
                                sx={{
                                    p: 4,
                                    borderRadius: 2,
                                    backgroundColor: theme.palette.mode === 'dark' ? 'neutral.800' : 'common.white',
                                }}
                            >
                                <Typography variant="h5" gutterBottom>
                                    {tr("4. Use of the Platform")}
                                </Typography>
                                <Typography
                                    variant="body1"
                                    sx={{ color: theme.palette.text.secondary, lineHeight: 1.7 }}
                                >
                                    {tr("You agree to use the Platform only for lawful purposes and in line with its intended purpose: connecting contractors and homeowners. You agree not to:")}
                                </Typography>
                                <Box component="ul" sx={{ pl: 3, mt: 1 }}>
                                    <li style={{ color: theme.palette.text.secondary }}>
                                        <Typography variant="body1" sx={{ color: theme.palette.text.secondary, lineHeight: 1.7 }}>
                                            {tr("Violate laws or regulations while using the Platform.")}
                                        </Typography>
                                    </li>
                                    <li style={{ color: theme.palette.text.secondary }}>
                                        <Typography variant="body1" sx={{ color: theme.palette.text.secondary, lineHeight: 1.7 }}>
                                            {tr("Post false, misleading, or harmful content.")}
                                        </Typography>
                                    </li>
                                    <li style={{ color: theme.palette.text.secondary }}>
                                        <Typography variant="body1" sx={{ color: theme.palette.text.secondary, lineHeight: 1.7 }}>
                                            {tr("Transmit viruses, malware, or harmful code.")}
                                        </Typography>
                                    </li>
                                    <li style={{ color: theme.palette.text.secondary }}>
                                        <Typography variant="body1" sx={{ color: theme.palette.text.secondary, lineHeight: 1.7 }}>
                                            {tr("Collect personal information without consent.")}
                                        </Typography>
                                    </li>
                                    <li style={{ color: theme.palette.text.secondary }}>
                                        <Typography variant="body1" sx={{ color: theme.palette.text.secondary, lineHeight: 1.7 }}>
                                            {tr("Resell, scrape, or misuse Platform data.")}
                                        </Typography>
                                    </li>
                                </Box>
                                <Typography
                                    variant="body1"
                                    sx={{ color: theme.palette.text.secondary, mt: 2 }}
                                >
                                    {tr("We reserve the right to suspend or terminate accounts that violate these Terms.")}
                                </Typography>

                                <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 3 }}>
                                    {tr("User-Generated Content and Objectionable Content")}
                                </Typography>
                                <Typography
                                    variant="body1"
                                    sx={{ color: theme.palette.text.secondary, lineHeight: 1.7 }}
                                >
                                    {tr("CTMASS lets users post projects, profiles, reviews, photos, and messages. There is zero tolerance for objectionable, abusive, or illegal content or behavior — including harassment, bullying, hate speech, threats, sexually explicit content, spam, or impersonation. You can report content or users, and block abusive users, directly within the app. We review reports and remove violating content, and may suspend or terminate accounts, typically within 24 hours of becoming aware of a violation.")}
                                </Typography>
                            </Paper>

                            {/* Term 5 */}
                            <Paper
                                elevation={2}
                                sx={{
                                    p: 4,
                                    borderRadius: 2,
                                    backgroundColor: theme.palette.mode === 'dark' ? 'neutral.800' : 'common.white',
                                }}
                            >
                                <Typography variant="h5" gutterBottom>
                                    {tr("5. Intellectual Property")}
                                </Typography>
                                <Typography
                                    variant="body1"
                                    sx={{
                                        color: theme.palette.text.secondary,
                                        lineHeight: 1.7,
                                    }}
                                >
                                    {tr("All content, trademarks, and materials on the Platform are owned by CTMASS or its licensors. You may not reproduce, distribute, or modify them without our prior written consent.")}
                                </Typography>
                            </Paper>

                            {/* Term 6 */}
                            <Paper
                                elevation={2}
                                sx={{
                                    p: 4,
                                    borderRadius: 2,
                                    backgroundColor: theme.palette.mode === 'dark' ? 'neutral.800' : 'common.white',
                                }}
                            >
                                <Typography variant="h5" gutterBottom>
                                    {tr("6. Disclaimer of Warranties")}
                                </Typography>
                                <Typography
                                    variant="body1"
                                    sx={{
                                        lineHeight: 1.7,
                                        color: theme.palette.text.secondary,
                                    }}
                                >
                                    {tr("The Platform is provided “as is” and “as available.” CTMASS makes no warranties, express or implied, regarding accuracy, reliability, availability, or suitability for a particular purpose.")}


                                </Typography>
                            </Paper>

                            {/* Term 7 */}
                            <Paper
                                elevation={2}
                                sx={{
                                    p: 4,
                                    borderRadius: 2,
                                    backgroundColor: theme.palette.mode === 'dark' ? 'neutral.800' : 'common.white',
                                }}
                            >
                                <Typography variant="h5" gutterBottom>
                                    {tr("7. Neutral Marketplace & Limitation of Liability")}
                                </Typography>
                                {/* 7.1 */}
                                <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 3 }}>
                                    {tr("7.1 Neutral Role")}
                                </Typography>
                                <Typography
                                    variant="body1"
                                    sx={{ color: theme.palette.text.secondary, mb: 2 }}
                                >
                                    {tr("CTMASS is a neutral platform that connects homeowners and contractors. We do not supervise, manage, or control the work performed by contractors or the payment practices of homeowners.")}
                                </Typography>

                                {/* 7.2 */}
                                <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 3 }}>
                                    {tr("7.2 Contractor Responsibility")}
                                </Typography>
                                <Typography
                                    variant="body1"
                                    sx={{ color: theme.palette.text.secondary }}
                                >
                                    {tr("Contractors are independent businesses. They are solely responsible for the quality, legality, safety, and timeliness of their work, including compliance with applicable laws, codes, and licensing requirements.")}
                                </Typography>
                                <Box component="ul" sx={{ pl: 3, mt: 1 }}>
                                    <li style={{ color: theme.palette.text.secondary }}>
                                        <Typography variant="body1" sx={{
                                            lineHeight: 1.7,
                                            color: theme.palette.text.secondary,
                                        }}>
                                            {tr("Example: If a contractor provides poor or unprofessional work, CTMASS is not responsible for correcting or paying for that work.")}
                                        </Typography>
                                    </li>
                                </Box>

                                {/* 7.3 */}
                                <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 3 }}>
                                    {tr("7.3 Homeowner Responsibility")}
                                </Typography>
                                <Typography
                                    variant="body1"
                                    sx={{ color: theme.palette.text.secondary }}
                                >
                                    {tr("Homeowners are solely responsible for negotiating, accepting, and paying for services. CTMASS is not a party to service agreements. Before hiring, homeowners should verify the contractor’s Connecticut or Massachusetts home improvement registration.")}
                                </Typography>
                                <Box component="ul" sx={{ pl: 3, mt: 1 }}>
                                    <li style={{ color: theme.palette.text.secondary }}>
                                        <Typography variant="body1" sx={{
                                            lineHeight: 1.7,
                                            color: theme.palette.text.secondary,
                                        }}>
                                            {tr("Example: If a homeowner refuses to pay after services are completed, CTMASS is not responsible for collecting or covering that payment.")}
                                        </Typography>
                                    </li>
                                </Box>

                                {/* 7.4 */}
                                <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 3 }}>
                                    {tr("7.4 No Guarantee")}
                                </Typography>
                                <Typography
                                    variant="body1"
                                    sx={{ color: theme.palette.text.secondary }}
                                >
                                    {tr("CTMASS does not guarantee that:")}
                                </Typography>
                                <Box component="ul" sx={{ pl: 3, mt: 1 }}>
                                    <li style={{ color: theme.palette.text.secondary }}>
                                        <Typography variant="body1" sx={{
                                            lineHeight: 1.7,
                                            color: theme.palette.text.secondary,
                                        }}>
                                            {tr("A contractor will complete work to the homeowner’s satisfaction, or")}
                                        </Typography>
                                    </li>
                                    <li style={{ color: theme.palette.text.secondary }}>
                                        <Typography variant="body1" sx={{
                                            lineHeight: 1.7,
                                            color: theme.palette.text.secondary,
                                        }}>
                                            {tr("A homeowner will pay in full or on time.")}
                                        </Typography>
                                    </li>
                                </Box>
                                <Typography
                                    variant="body1"
                                    sx={{ color: theme.palette.text.secondary, mt: 1 }}
                                >
                                    {tr("All risks arising from user interactions remain solely with contractors and homeowners.")}
                                </Typography>

                                {/* 7.5 */}
                                <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 3 }}>
                                    {tr("7.5 Limitation of Liability")}
                                </Typography>
                                <Typography
                                    variant="body1"
                                    sx={{ color: theme.palette.text.secondary }}
                                >
                                    {tr("To the fullest extent permitted by law, CTMASS LLC shall not be liable for any damages, losses, or disputes arising out of or related to interactions between contractors and homeowners.")}
                                </Typography>
                            </Paper>

                            {/* Term 8 */}
                            <Paper
                                elevation={2}
                                sx={{
                                    p: 4,
                                    borderRadius: 2,
                                    backgroundColor: theme.palette.mode === 'dark' ? 'neutral.800' : 'common.white',
                                }}
                            >
                                <Typography variant="h5" gutterBottom>
                                    {tr("8. Indemnification")}
                                </Typography>
                                <Typography
                                    variant="body1"
                                    sx={{
                                        color: theme.palette.text.secondary,
                                        lineHeight: 1.7,
                                    }}
                                >
                                    {tr("You agree to indemnify and hold harmless CTMASS LLC, its officers, employees, and agents from any claims, damages, or expenses resulting from your use of the Platform or your violation of these Terms.")}
                                </Typography>
                            </Paper>

                            {/* Term 9 */}
                            <Paper
                                elevation={2}
                                sx={{
                                    p: 4,
                                    borderRadius: 2,
                                    backgroundColor: theme.palette.mode === 'dark' ? 'neutral.800' : 'common.white',
                                }}
                            >
                                <Typography variant="h5" gutterBottom>
                                    {tr("9. Changes to These Terms")}
                                </Typography>
                                <Typography
                                    variant="body1"
                                    sx={{
                                        color: theme.palette.text.secondary,
                                        lineHeight: 1.7,
                                    }}
                                >
                                    {tr("We may update these Terms from time to time. Updates will be posted on CTMASS.com, and continued use of the Platform means you accept the revised Terms.")}
                                </Typography>
                            </Paper>

                            {/* <Paper
                                elevation={2}
                                sx={{
                                    p: 4,
                                    borderRadius: 2,
                                    backgroundColor: theme.palette.mode === 'dark' ? 'neutral.800' : 'common.white',
                                }}
                            >
                                <Typography variant="h5" gutterBottom>
                                    10. Dispute Resolution
                                </Typography>
                                <Typography
                                    variant="body1"
                                    sx={{
                                        color: theme.palette.text.secondary,
                                        lineHeight: 1.7,
                                    }}
                                >
                                    Any dispute arising out of or relating to these Terms shall be resolved exclusively by mediation in Amherst, Massachusetts. The mediation shall be conducted by a mutually agreed-upon mediator. If the parties cannot agree on a mediator within thirty (30) days of one party providing written notice of a dispute, the mediator shall be appointed by the Franklin/Hampshire Probate and Family Court.
                                </Typography>
                            </Paper>

                            <Paper
                                elevation={2}
                                sx={{
                                    p: 4,
                                    borderRadius: 2,
                                    backgroundColor: theme.palette.mode === 'dark' ? 'neutral.800' : 'common.white',
                                }}
                            >
                                <Typography variant="h5" gutterBottom>
                                    11. Changes to These Terms
                                </Typography>
                                <Typography
                                    variant="body1"
                                    sx={{
                                        color: theme.palette.text.secondary,
                                        lineHeight: 1.7,
                                    }}
                                >
                                    CTMASS.com may update these Terms from time to time. We will notify you of any material changes by posting the updated Terms on the Platform. You are advised to review these Terms periodically for any changes. Your continued use of the Platform after the posting of any changes to these Terms constitutes your acceptance of such changes.
                                </Typography>
                            </Paper> */}

                            {/* Term 12 */}
                            <Paper
                                elevation={2}
                                sx={{
                                    p: 4,
                                    borderRadius: 2,
                                    backgroundColor: theme.palette.mode === 'dark' ? 'neutral.800' : 'common.white',
                                }}
                            >
                                <Typography variant="h5" gutterBottom>
                                    {tr("10. Contact Information")}
                                </Typography>
                                <Typography
                                    variant="body1"
                                    sx={{ color: theme.palette.text.secondary, lineHeight: 1.7 }}
                                >
                                    {tr("For questions, contact us at:")}
                                    <Link href="mailto:support@ctmass.com" sx={{ fontWeight: 700 }}>
                                        support@ctmass.com
                                    </Link>
                                </Typography>

                                <FormGroup sx={{ mt: 2 }}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={agreeTos}
                                                onChange={handleAgreeChange}
                                            />
                                        }
                                        label={tr("By checking a box, you consent with terms and conditions")}
                                    />
                                </FormGroup>

                                {agreeTos && (
                                    <Alert
                                        severity="success"
                                        icon={<CheckCircleIcon fontSize="inherit" />}
                                        sx={{ mt: 1 }}
                                    >
                                        {tr("You have accepted the Terms and Conditions{{0}}", { 0: (user?.id ? tr(" — your choice has been saved to your account.") : '.') })}
                                    </Alert>
                                )}

                                {/* Notifications */}
                                <Typography
                                    variant="h6"
                                    sx={{ mt: 4, mb: 1, fontWeight: 600 }}
                                >
                                    {tr("Notifications and Communications")}
                                </Typography>
                                <Typography variant="body1">
                                    {tr("Notification frequency is managed via")}{' '}<strong>{tr("Account Settings → Notifications")}</strong>{tr(". Available options: Immediately, Once a day, Once every three days, Once a week, Once a month, or complete opt-out.")}
                                </Typography>
                            </Paper>
                        </Stack>
                    </Container>
                </Box>
            </Box>
            <HomeCta />
        </>
    );
};

export default Page;
